import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Sidebar, { SidebarProvider } from '../../components/navigation/Sidebar';
import { supabase } from '../../lib/supabase';
import { wordpressService } from '../../services/wordpressService';
import { fetchArticlesFast } from '../../services/articleScraperService';
import { useModal } from '../../components/ui/ModalProvider';
import { fetchThreadsPosts, deleteThreadsPost, fetchThreadsAccounts } from '../../services/threadsPostsService';
import { useLanguageProfile } from '../../contexts/LanguageProfileContext';
import { useRequestGuard } from '../../hooks/useRequestGuard';
import { generateHumanSchedule, toDatetimeLocalValue } from '../../utils/humanSchedule';
import {
  Plus, Search, RefreshCw, Calendar, Link as LinkIcon, X, Clock, Check,
  ChevronLeft, ChevronRight, ChevronDown, Trash2, Eye, Edit, Globe, Layers, Zap, Send,
  Image as ImageIcon, AlertCircle, Instagram,
} from 'lucide-react';

const FUNCTIONS_URL = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;
const ANON_KEY      = import.meta.env.VITE_SUPABASE_ANON_KEY;
const ARTICLE_PAGE  = 30;

async function getAccessToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Spinner({ size = 4, color = 'indigo' }) {
  const cls = color === 'white'
    ? 'border-white/30 border-t-white'
    : 'border-gray-300 border-t-gray-600';
  return (
    <div className={`w-${size} h-${size} border-2 ${cls} rounded-full animate-spin flex-shrink-0`} />
  );
}

function ThreadsLogoIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 192 192" fill="currentColor" aria-hidden="true">
      <path d="M141.537 88.988c-2.406-1.125-4.876-2.107-7.404-2.94-.78-3.169-1.8-6.076-3.034-8.668
        C125.19 62.595 113.617 55.244 99.967 55.244c-9.547 0-18.386 3.463-25.064 9.914
        C68.8 71.01 65.15 79.148 64.374 88.617a54.27 54.27 0 0 0-7.386 2.97l1.944 3.4
        a50.46 50.46 0 0 1 5.876-2.347c-.266 3.44-.186 7.12.298 10.987
        a46.06 46.06 0 0 0 2.352 9.196c6.726 18.278 23.41 28.98 43.77 28.98
        c7.822 0 15.006-1.82 21.345-5.374a37.22 37.22 0 0 0 8.673-7.0
        c5.047-5.69 8.118-13.104 8.273-21.397a64.33 64.33 0 0 1 5.666 2.008l2.155-3.762
        c-7.46-3.015-15.614-4.798-23.993-4.29zm-41.57 31.23
        c-16.624 0-29.546-9.223-35.18-24.443a39.96 39.96 0 0 1-1.927-7.317
        c-.628-4.347-.458-8.535.268-12.398
        c1.16-6.368 3.892-11.672 7.926-15.433
        C76.096 56.15 87.035 51.46 105.967 51.46
        c17.7 0 30.215 8.147 36.166 22.785.842 2.043 1.576 4.163 2.196 6.346
        a80.58 80.58 0 0 0-11.34-.509c-5.875 0-11.388.534-16.422 1.569v4.106
        c4.873-1.018 10.212-1.548 15.904-1.548 4.444 0 8.75.362 12.889 1.042
        .263 4.262-.208 8.326-1.359 12.026-1.157 3.803-3.083 7.172-5.68 9.967
        a30.47 30.47 0 0 1-6.975 5.685c-5.247 3.022-11.487 4.618-18.544 4.618z"/>
    </svg>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CreateThread() {
  const { alert, confirm } = useModal();
  const { activeProfile } = useLanguageProfile();
  const startPostsRequest = useRequestGuard();
  const startAccountsRequest = useRequestGuard();
  const startInstagramAccountsRequest = useRequestGuard();
  const startWebsitesRequest = useRequestGuard();
  const startArticlesRequest = useRequestGuard();

  // ── Table state ─────────────────────────────────────────────────────────
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [accountFilter, setAccountFilter] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusCounts, setStatusCounts] = useState({ all: 0, scheduled: 0, published: 0, draft: 0, failed: 0 });

  // Table pagination
  const TABLE_PAGE_SIZE = 40;
  const [tablePage, setTablePage] = useState(1);

  // Bulk selection
  const [selectedPostIds, setSelectedPostIds] = useState(new Set());
  const selectAllRef = useRef(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const searchedPosts = searchQuery.trim()
    ? posts.filter((post) => (post.caption || '').toLowerCase().includes(searchQuery.trim().toLowerCase()))
    : posts;
  const totalTablePages = Math.max(1, Math.ceil(searchedPosts.length / TABLE_PAGE_SIZE));
  const safeTablePage = Math.min(tablePage, totalTablePages);
  const pagedPosts = searchedPosts.slice((safeTablePage - 1) * TABLE_PAGE_SIZE, safeTablePage * TABLE_PAGE_SIZE);
  const selectedOnPageCount = pagedPosts.filter((post) => selectedPostIds.has(post.id)).length;
  const isAllOnPageSelected = pagedPosts.length > 0 && selectedOnPageCount === pagedPosts.length;

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = selectedOnPageCount > 0 && selectedOnPageCount < pagedPosts.length;
  }, [selectedOnPageCount, pagedPosts.length]);

  useEffect(() => {
    setSelectedPostIds((previous) => {
      if (previous.size === 0) return previous;
      const validIds = new Set(posts.map((post) => post.id));
      const next = new Set([...previous].filter((id) => validIds.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [posts]);

  useEffect(() => {
    setSelectedPostIds(new Set());
  }, [tablePage]);

  const toggleSelectPost = (postId) => {
    setSelectedPostIds((previous) => {
      const next = new Set(previous);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedPostIds((previous) => {
      const next = new Set(previous);
      pagedPosts.forEach((post) => {
        if (isAllOnPageSelected) next.delete(post.id);
        else next.add(post.id);
      });
      return next;
    });
  };

  // Threads accounts (shared by filter dropdown + New Thread modal)
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);

  // Instagram accounts stay independent from Threads accounts and are
  // always loaded through the active Gymplix profile.
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [instagramAccountsLoading, setInstagramAccountsLoading] = useState(true);
  const [selectedInstagramAccountId, setSelectedInstagramAccountId] = useState('');
  const [publishToThreads, setPublishToThreads] = useState(true);
  const [publishToInstagram, setPublishToInstagram] = useState(false);

  // New Thread modal
  const [showModal, setShowModal] = useState(false);

  // Article browser
  const [websites, setWebsites]               = useState([]);
  const [selectedWebsiteId, setSelectedWebsiteId] = useState('');
  const [websiteArticles, setWebsiteArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articlesError, setArticlesError]     = useState('');
  const [articleOffset, setArticleOffset]     = useState(0);
  const [hasMoreArticles, setHasMoreArticles] = useState(false);

  // Queue
  const [bulkQueue, setBulkQueue]         = useState([]);
  const [publishStartDate, setPublishStartDate] = useState('');
  const [bulkInterval, setBulkInterval]   = useState(110);
  const [threadsPerDay, setThreadsPerDay] = useState(0);

  // Submission
  const [submitting, setSubmitting]         = useState(false);
  const [submitAction, setSubmitAction]     = useState('schedule'); // 'schedule' | 'publish'
  const [submitProgress, setSubmitProgress] = useState({ done: 0, total: 0 });
  const [notification, setNotification]     = useState(null);

  // Edit modal
  const [showEditModal, setShowEditModal]   = useState(false);
  const [editingPost, setEditingPost]       = useState(null);
  const [editCaption, setEditCaption]       = useState('');
  const [editScheduledAt, setEditScheduledAt] = useState('');
  const [editSaving, setEditSaving]         = useState(false);

  // Clear the previous profile's posts/accounts immediately on switch so
  // stale content can't flash while the new profile's data is loading. Also
  // drop the cached article-importer website list so the modal re-fetches
  // it scoped to the newly active profile instead of reusing another
  // profile's.
  useEffect(() => {
    startArticlesRequest();
    setPosts([]);
    setAccounts([]);
    setInstagramAccounts([]);
    setSelectedInstagramAccountId('');
    setPublishToThreads(true);
    setPublishToInstagram(false);
    setWebsites([]);
    setSelectedWebsiteId('');
    setWebsiteArticles([]);
    setArticlesError('');
    setArticlesLoading(false);
    setArticleOffset(1);
    setHasMoreArticles(false);
  }, [activeProfile?.id, startArticlesRequest]);

  // ── Load posts (table) ────────────────────────────────────────────────────
  const loadPosts = useCallback(async () => {
    const isCurrent = startPostsRequest();
    setPostsLoading(true);
    try {
      const data = await fetchThreadsPosts({
        status: statusFilter,
        accountId: accountFilter,
        startDate: dateRange.start,
        endDate: dateRange.end,
        profileId: activeProfile?.id || null,
      });
      if (!isCurrent()) return;
      setPosts(data);

      const allStatusPosts = statusFilter === 'all'
        ? data
        : await fetchThreadsPosts({
            status: 'all',
            accountId: accountFilter,
            startDate: dateRange.start,
            endDate: dateRange.end,
            profileId: activeProfile?.id || null,
          });
      if (!isCurrent()) return;
      const counts = { all: allStatusPosts.length, scheduled: 0, published: 0, draft: 0, failed: 0 };
      allStatusPosts.forEach((post) => {
        if (counts[post.status] !== undefined) counts[post.status] += 1;
      });
      setStatusCounts(counts);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('[ThreadPlanner] Error loading threads:', error);
      setPosts([]);
    } finally {
      if (isCurrent()) {
        setPostsLoading(false);
        setLoading(false);
      }
    }
  }, [statusFilter, accountFilter, dateRange.start, dateRange.end, activeProfile?.id, startPostsRequest]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  // ── Load Threads accounts ─────────────────────────────────────────────────
  useEffect(() => {
    const isCurrent = startAccountsRequest();
    const loadAccounts = async () => {
      setAccountsLoading(true);
      try {
        const data = await fetchThreadsAccounts(activeProfile?.id);
        if (!isCurrent()) return;
        setAccounts(data);
        setSelectedAccountIds(data.map((a) => a.id));
      } catch (e) {
        if (!isCurrent()) return;
        console.error('[ThreadPlanner] loadAccounts:', e);
      } finally {
        if (isCurrent()) setAccountsLoading(false);
      }
    };
    loadAccounts();
  }, [activeProfile?.id, startAccountsRequest]);

  // ── Load Instagram accounts ──────────────────────────────────────────────
  useEffect(() => {
    const isCurrent = startInstagramAccountsRequest();
    const loadInstagramAccounts = async () => {
      setInstagramAccountsLoading(true);
      try {
        if (!activeProfile?.id) {
          if (isCurrent()) {
            setInstagramAccounts([]);
            setSelectedInstagramAccountId('');
          }
          return;
        }

        const token = await getAccessToken();
        if (!token) throw new Error('Authentication expired');
        const res = await fetch(
          `${FUNCTIONS_URL}/instagram-accounts?profileId=${encodeURIComponent(activeProfile.id)}`,
          { headers: { Authorization: `Bearer ${token}`, apikey: ANON_KEY } }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to load Instagram accounts');
        if (!isCurrent()) return;

        const profileAccounts = data.accounts || [];
        setInstagramAccounts(profileAccounts);
        setSelectedInstagramAccountId((current) =>
          profileAccounts.some((account) => account.id === current)
            ? current
            : profileAccounts[0]?.id || ''
        );
        if (profileAccounts.length === 0) setPublishToInstagram(false);
      } catch (error) {
        if (!isCurrent()) return;
        console.error('[ThreadPlanner] Instagram accounts:', error);
        setInstagramAccounts([]);
        setSelectedInstagramAccountId('');
        setPublishToInstagram(false);
      } finally {
        if (isCurrent()) setInstagramAccountsLoading(false);
      }
    };
    loadInstagramAccounts();
  }, [activeProfile?.id, startInstagramAccountsRequest]);

  const accountsById = useMemo(() => {
    const map = {};
    accounts.forEach((a) => { map[a.id] = a; });
    return map;
  }, [accounts]);

  // ── Load WordPress sites when modal opens ────────────────────────────────
  useEffect(() => {
    if (showModal && websites.length === 0) {
      const isCurrent = startWebsitesRequest();
      wordpressService.getAllSites(activeProfile?.id)
        .then((sites) => { if (isCurrent()) setWebsites(sites || []); })
        .catch((err) => { if (isCurrent()) console.error('Error loading websites:', err); });
    }
  }, [showModal, websites.length, activeProfile?.id, startWebsitesRequest]);

  const handleResetFilters = () => {
    setStatusFilter('all');
    setAccountFilter('');
    setDateRange({ start: '', end: '' });
    setSearchQuery('');
    loadPosts();
  };

  const handleFilterChange = () => {
    loadPosts();
  };

  // ── Article browser ───────────────────────────────────────────────────────
  const loadArticles = useCallback(async (siteId, page, append) => {
    const isCurrent = startArticlesRequest();
    const site = websites.find((item) => String(item.id) === String(siteId));

    if (!site || (activeProfile?.id && String(site.profileId) !== String(activeProfile.id))) {
      setWebsiteArticles([]);
      setArticlesError('The selected website is not available in the active profile.');
      setArticlesLoading(false);
      return;
    }
    if (!site.siteUrl) {
      setWebsiteArticles([]);
      setArticlesError('The selected website does not have a WordPress URL.');
      setArticlesLoading(false);
      return;
    }

    setArticlesError('');
    setArticlesLoading(true);
    try {
      const fetched = await fetchArticlesFast(site.siteUrl, ARTICLE_PAGE, page, { throwOnFailure: true });
      if (!isCurrent()) return;

      const items = fetched.map((article) => ({
        id: article.articleUrl,
        databaseId: null,
        title: article.title,
        image_main_url: article.featuredImageUrl || null,
        focus_keyword: article.focusKeyword || '',
        meta_description: article.recipeCardDescription || '',
        wp_post_url: article.articleUrl,
        created_at: article.publishDate || null,
        language: activeProfile?.language_code || 'en',
      }));

      setHasMoreArticles(items.length === ARTICLE_PAGE);
      setArticleOffset(page + 1);

      if (append) {
        setWebsiteArticles((prev) => {
          const existing = new Set(prev.map((article) => article.id));
          return [...prev, ...items.filter((article) => !existing.has(article.id))];
        });
      } else {
        setWebsiteArticles(items);
      }
    } catch (e) {
      if (!isCurrent()) return;
      console.error('[ThreadPlanner] loadArticles:', e);
      setArticlesError(e?.message || 'Unable to fetch published WordPress articles.');
      if (!append) setWebsiteArticles([]);
    } finally {
      if (isCurrent()) setArticlesLoading(false);
    }
  }, [activeProfile?.id, activeProfile?.language_code, startArticlesRequest, websites]);

  const handleWebsiteChange = (siteId) => {
    setSelectedWebsiteId(siteId);
    setWebsiteArticles([]);
    setArticlesError('');
    setArticleOffset(1);
    setHasMoreArticles(false);
    if (!siteId) {
      startArticlesRequest();
      setArticlesLoading(false);
      return;
    }
    loadArticles(siteId, 1, false);
  };

  const handleShowMore = () => loadArticles(selectedWebsiteId, articleOffset, true);

  // ── Queue management ──────────────────────────────────────────────────────
  const handleArticleToggle = async (article) => {
    const inQueue = bulkQueue.some((item) => item.article.id === article.id);
    if (inQueue) {
      setBulkQueue((prev) => prev.filter((item) => item.article.id !== article.id));
      return;
    }

    const queueId = `${article.id}-${Date.now()}`;
    setBulkQueue((prev) => [
      ...prev,
      {
        id:            queueId,
        article,
        caption:       article.meta_description || '',
        firstComment:  '',
        imageUrl:      article.image_main_url || '',
        scheduledTime: '',
        loading:       true,
      },
    ]);

    try {
      const token = await getAccessToken();
      const res = await fetch(`${FUNCTIONS_URL}/generate-thread-content`, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization:  `Bearer ${token}`,
          apikey:         ANON_KEY,
        },
        body: JSON.stringify({
          title:      article.title,
          keyword:    article.focus_keyword,
          excerpt:    article.meta_description,
          articleUrl: article.wp_post_url,
          language:   article.language || 'en',
        }),
      });
      const data = await res.json().catch(() => ({}));
      setBulkQueue((prev) =>
        prev.map((item) =>
          item.id === queueId
            ? {
                ...item,
                caption:      item.caption,
                firstComment: (res.ok && data.first_comment) || article.wp_post_url || '',
                loading:      false,
              }
            : item
        )
      );
    } catch (e) {
      console.error('[ThreadPlanner] generate-thread-content:', e);
      setBulkQueue((prev) =>
        prev.map((item) =>
          item.id === queueId
            ? { ...item, firstComment: article.wp_post_url || '', loading: false }
            : item
        )
      );
    }
  };

  const allVisibleArticlesSelected =
    websiteArticles.length > 0 &&
    websiteArticles.every((a) => bulkQueue.some((item) => item.article.id === a.id));

  const handleSelectAllToggle = () => {
    if (allVisibleArticlesSelected) {
      const visibleIds = new Set(websiteArticles.map((a) => a.id));
      setBulkQueue((prev) => prev.filter((item) => !visibleIds.has(item.article.id)));
    } else {
      websiteArticles.forEach((article) => {
        const inQueue = bulkQueue.some((item) => item.article.id === article.id);
        if (!inQueue) handleArticleToggle(article);
      });
    }
  };

  const handleQueueUpdate = (id, field, value) => {
    setBulkQueue((prev) => prev.map((item) => item.id === id ? { ...item, [field]: value } : item));
  };

  const handleRemoveRow = (id) => {
    setBulkQueue((prev) => prev.filter((item) => item.id !== id));
  };

  const generateSchedule = () => {
    if (bulkQueue.length === 0 || !publishStartDate) return;
    if (!publishToThreads && !publishToInstagram) {
      alert('Select Publish on Threads, Publish on Instagram, or both.');
      return;
    }
    if (publishToInstagram && !selectedInstagramAccountId) {
      alert('Please select an Instagram account.');
      return;
    }
    const effectiveInterval = threadsPerDay > 0 ? Math.round((24 * 60) / threadsPerDay) : bulkInterval;
    const firstPostDate = new Date(publishStartDate);
    if (Number.isNaN(firstPostDate.getTime())) return;
    const followingSchedule = generateHumanSchedule({
      platform: 'threads',
      count: Math.max(0, bulkQueue.length - 1),
      baseDate: new Date(firstPostDate.getTime() + effectiveInterval * 60000),
      intervalMinutes: effectiveInterval,
    });
    const schedule = [firstPostDate, ...followingSchedule];
    setBulkQueue((prev) =>
      prev.map((item, i) => ({ ...item, scheduledTime: toDatetimeLocalValue(schedule[i]) }))
    );
  };

  // ── Account selection (inside modal) ──────────────────────────────────────
  const toggleAccount = (id) => {
    setSelectedAccountIds((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  // ── Reset modal form ──────────────────────────────────────────────────────
  const closeModal = () => {
    startArticlesRequest();
    setShowModal(false);
    setSelectedWebsiteId('');
    setWebsiteArticles([]);
    setArticlesError('');
    setArticlesLoading(false);
    setArticleOffset(1);
    setHasMoreArticles(false);
    setBulkQueue([]);
    setPublishStartDate('');
    setBulkInterval(110);
    setThreadsPerDay(0);
    setPublishToThreads(true);
    setPublishToInstagram(false);
    setSelectedInstagramAccountId(instagramAccounts[0]?.id || '');
    setSubmitProgress({ done: 0, total: 0 });
  };

  // ── Submit / schedule ──────────────────────────────────────────────────────
  const handleSubmit = async (publishNow = false) => {
    if (bulkQueue.length === 0) {
      alert('Please select at least one article.');
      return;
    }
    if (!publishToThreads && !publishToInstagram) {
      alert('Select Publish on Threads, Publish on Instagram, or both.');
      return;
    }
    if (publishToThreads && selectedAccountIds.length === 0) {
      alert('Please select at least one Threads account.');
      return;
    }
    if (publishToInstagram && !selectedInstagramAccountId) {
      alert('Please select an Instagram account.');
      return;
    }
    if (publishToInstagram && bulkQueue.some((item) => !item.imageUrl)) {
      alert('Every Instagram post requires an article image.');
      return;
    }

    const queueSnapshot   = [...bulkQueue];
    const accountSnapshot = [...selectedAccountIds];
    const destinationsPerArticle =
      (publishToThreads ? accountSnapshot.length : 0) + (publishToInstagram ? 1 : 0);
    const total = queueSnapshot.length * destinationsPerArticle;

    setSubmitAction(publishNow ? 'publish' : 'schedule');
    setSubmitting(true);
    setSubmitProgress({ done: 0, total });

    const token = await getAccessToken();
    if (!token) {
      alert('Session expired — please reload and try again.');
      setSubmitting(false);
      return;
    }

    const threadResults = { posted: 0, scheduled: 0, failed: 0 };
    const instagramResults = { posted: 0, scheduled: 0, failed: 0 };

    for (const item of queueSnapshot) {
      const scheduledAt = !publishNow && item.scheduledTime ? item.scheduledTime + ':00Z' : null;

      // Keep the existing Threads publishing request and result handling
      // independent from the Instagram request below.
      if (publishToThreads) {
        for (const accountId of accountSnapshot) {
          try {
            const res = await fetch(`${FUNCTIONS_URL}/publish-thread`, {
              method:  'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization:  `Bearer ${token}`,
                apikey:         ANON_KEY,
              },
              body: JSON.stringify({
                accountId,
                caption:      item.caption.trim() || item.article.title,
                imageUrl:     item.imageUrl || null,
                articleId:    item.article.databaseId || null,
                firstComment: item.firstComment?.trim() || null,
                scheduledAt,
              }),
            });

            if (res.ok) {
              const data = await res.json().catch(() => ({}));
              if (data.status === 'scheduled') {
                threadResults.scheduled++;
              } else {
                threadResults.posted++;
              }
            } else {
              threadResults.failed++;
            }
          } catch {
            threadResults.failed++;
          }
          setSubmitProgress((prev) => ({ ...prev, done: prev.done + 1 }));
        }
      }

      if (publishToInstagram) {
        try {
          const res = await fetch(`${FUNCTIONS_URL}/publish-instagram`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              apikey: ANON_KEY,
            },
            body: JSON.stringify({
              accountId: selectedInstagramAccountId,
              profileId: activeProfile?.id,
              caption: item.caption.trim() || item.article.title,
              imageUrl: item.imageUrl,
              articleId: item.article.databaseId || null,
              scheduledAt,
            }),
          });

          if (res.ok) {
            const data = await res.json().catch(() => ({}));
            if (data.status === 'scheduled') {
              instagramResults.scheduled++;
            } else {
              instagramResults.posted++;
            }
          } else {
            instagramResults.failed++;
          }
        } catch {
          instagramResults.failed++;
        }
        setSubmitProgress((prev) => ({ ...prev, done: prev.done + 1 }));
      }
    }

    setSubmitting(false);

    const totalOk = threadResults.posted + threadResults.scheduled
      + instagramResults.posted + instagramResults.scheduled;
    if (totalOk > 0) {
      const parts = [];
      if (publishToThreads) {
        const threadParts = [];
        if (threadResults.posted > 0) threadParts.push(`${threadResults.posted} posted`);
        if (threadResults.scheduled > 0) threadParts.push(`${threadResults.scheduled} scheduled`);
        if (threadResults.failed > 0) threadParts.push(`${threadResults.failed} failed`);
        parts.push(`Threads: ${threadParts.join(', ')}`);
      }
      if (publishToInstagram) {
        const instagramParts = [];
        if (instagramResults.posted > 0) instagramParts.push(`${instagramResults.posted} posted`);
        if (instagramResults.scheduled > 0) instagramParts.push(`${instagramResults.scheduled} scheduled`);
        if (instagramResults.failed > 0) instagramParts.push(`${instagramResults.failed} failed`);
        parts.push(`Instagram: ${instagramParts.join(', ')}`);
      }
      setNotification({
        type:    'success',
        message: `${parts.join(' · ')}!`,
      });
      closeModal();
      if (publishToThreads) await loadPosts();
      setTimeout(() => setNotification(null), 5000);
    } else {
      const failures = [];
      if (publishToThreads) failures.push(`Threads: ${threadResults.failed} failed`);
      if (publishToInstagram) failures.push(`Instagram: ${instagramResults.failed} failed`);
      alert(`Publishing failed. ${failures.join(' · ')}`);
    }
  };

  // ── Row actions ──────────────────────────────────────────────────────────
  const handleViewPost = (post) => {
    if (post.post_url) {
      window.open(post.post_url, '_blank');
    } else {
      alert('No Threads post URL available for this thread yet.');
    }
  };

  const handleDeletePost = async (postId) => {
    const confirmed = await alert('Are you sure you want to delete this thread?', {
      title: 'Delete Thread',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (!confirmed) return;

    try {
      await deleteThreadsPost(postId);
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      setNotification({ type: 'success', message: 'Thread deleted successfully!' });
      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error('Error deleting thread:', error);
      alert(`Failed to delete thread: ${error.message}`);
    }
  };

  const handleBulkDeleteSelected = async () => {
    const idsToDelete = Array.from(selectedPostIds);
    if (idsToDelete.length === 0) return;

    const confirmed = await confirm(
      `Delete ${idsToDelete.length} selected thread${idsToDelete.length !== 1 ? 's' : ''}? The selected threads will be removed from the Create Thread / Instagram page.`,
      {
        title: 'Delete Selected Threads',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      }
    );
    if (!confirmed) return;

    setBulkDeleting(true);
    try {
      await Promise.all(idsToDelete.map((postId) => deleteThreadsPost(postId)));
      const deletedIds = new Set(idsToDelete);
      setPosts((previous) => previous.filter((post) => !deletedIds.has(post.id)));
      setSelectedPostIds(new Set());
      setNotification({
        type: 'success',
        message: `${idsToDelete.length} thread${idsToDelete.length !== 1 ? 's' : ''} deleted successfully!`,
      });
      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error('Error bulk deleting threads:', error);
      alert(`Failed to delete selected threads: ${error.message}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setEditCaption(post.caption || '');
    setEditScheduledAt(
      post.scheduled_at ? new Date(post.scheduled_at).toISOString().slice(0, 16) : ''
    );
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editCaption.trim()) {
      alert('Caption cannot be empty');
      return;
    }
    setEditSaving(true);
    try {
      const { error } = await supabase
        .from('threads_scheduled_posts')
        .update({
          caption: editCaption,
          scheduled_at: editScheduledAt ? new Date(editScheduledAt).toISOString() : editingPost.scheduled_at,
        })
        .eq('id', editingPost.id);

      if (error) throw error;

      setShowEditModal(false);
      setEditingPost(null);
      await loadPosts();
      setNotification({ type: 'success', message: 'Thread updated successfully!' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      console.error('Error updating thread:', err);
      alert(`Failed to update thread: ${err.message}`);
    } finally {
      setEditSaving(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const getStatusBadge = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-700',
      published: 'bg-green-100 text-green-700',
      failed:    'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const truncateContent = (content, maxLength = 100) => {
    if (!content) return '—';
    return content.length > maxLength ? content.substring(0, maxLength) + '...' : content;
  };

  const scheduledCount = bulkQueue.filter((i) => i.scheduledTime).length;
  const hasPublishDestination = publishToThreads || publishToInstagram;
  const publishDestinationsReady = hasPublishDestination
    && (!publishToThreads || selectedAccountIds.length > 0)
    && (!publishToInstagram || Boolean(selectedInstagramAccountId));

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background overflow-x-hidden md:overflow-x-visible">
        <Sidebar />

        <main className="main-content">
          {/* Notification */}
          {notification && (
            <div className="fixed top-3 left-3 right-3 sm:top-4 sm:left-auto sm:right-4 z-50 animate-fade-in">
              <div className="bg-green-500 text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
                <Check className="w-5 h-5" />
                {notification.message}
              </div>
            </div>
          )}

          <div className="p-3 sm:p-6 space-y-6">
            {/* Header */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-12 h-12 rounded-xl bg-[#232323] flex items-center justify-center text-white">
                    <ThreadsLogoIcon size={22} />
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-xl font-bold text-gray-900">Create Thread / Instagram</h1>
                    <p className="text-sm text-gray-500">
                      Schedule and manage Threads and Instagram posts from one queue
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setShowModal(true)}
                  className="w-full sm:w-auto justify-center inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gray-900 text-white font-medium text-sm hover:bg-gray-700 transition-all shadow-md hover:shadow-lg"
                >
                  <Plus className="w-5 h-5" />
                  New Post
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white border border-gray-200/80 rounded-2xl shadow-sm p-3 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-center">
                <div className="relative w-full sm:flex-1 sm:min-w-[220px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search posts by content…"
                    className="w-full h-10 pl-9 pr-4 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div className="relative w-full sm:w-auto sm:min-w-[180px]">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-500 pointer-events-none" />
                  <select
                    value={accountFilter}
                    onChange={(e) => {
                      setAccountFilter(e.target.value);
                      handleFilterChange();
                    }}
                    className="w-full h-10 rounded-xl border border-gray-200 pl-9 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white appearance-none"
                  >
                    <option value="">All Threads Accounts</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>{account.name || account.username}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <span className="text-xs text-gray-400 whitespace-nowrap">From</span>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => {
                      setDateRange((previous) => ({ ...previous, start: e.target.value }));
                      handleFilterChange();
                    }}
                    className="flex-1 min-w-0 sm:flex-none h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <span className="text-xs text-gray-400 whitespace-nowrap">To</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => {
                      setDateRange((previous) => ({ ...previous, end: e.target.value }));
                      handleFilterChange();
                    }}
                    className="flex-1 min-w-0 sm:flex-none h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {(searchQuery || dateRange.start || dateRange.end || (statusFilter && statusFilter !== 'all') || accountFilter) && (
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="w-full sm:w-auto h-10 px-3 rounded-xl border border-gray-200 text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors whitespace-nowrap"
                  >
                    Clear filters
                  </button>
                )}

                <button
                  onClick={handleBulkDeleteSelected}
                  disabled={selectedPostIds.size === 0 || bulkDeleting}
                  className="w-full sm:w-auto justify-center px-4 py-2 rounded-lg bg-red-50 text-red-600 text-sm hover:bg-red-100 transition-colors flex items-center gap-2 sm:ml-auto disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Selected{selectedPostIds.size > 0 ? ` (${selectedPostIds.size})` : ''}
                </button>

                <button
                  onClick={loadPosts}
                  disabled={postsLoading}
                  className="w-full sm:w-auto justify-center px-4 py-2 rounded-lg bg-indigo-50 text-indigo-600 text-sm hover:bg-indigo-100 transition-colors flex items-center gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${postsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                {[
                  { key: 'all', label: 'All', count: statusCounts.all, dot: 'bg-gray-400', active: 'bg-gray-900 text-white shadow-sm', inactive: 'bg-gray-100 text-gray-600 hover:bg-gray-200' },
                  { key: 'scheduled', label: 'Scheduled', count: statusCounts.scheduled, dot: 'bg-blue-500', active: 'bg-blue-600 text-white shadow-sm', inactive: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
                  { key: 'published', label: 'Published', count: statusCounts.published, dot: 'bg-emerald-500', active: 'bg-emerald-600 text-white shadow-sm', inactive: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
                  { key: 'draft', label: 'Draft', count: statusCounts.draft, dot: 'bg-slate-400', active: 'bg-slate-600 text-white shadow-sm', inactive: 'bg-slate-100 text-slate-600 hover:bg-slate-200' },
                  { key: 'failed', label: 'Failed', count: statusCounts.failed, dot: 'bg-red-500', active: 'bg-red-600 text-white shadow-sm', inactive: 'bg-red-50 text-red-700 hover:bg-red-100' },
                ].map(({ key, label, count, dot, active, inactive }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setStatusFilter(key);
                      handleFilterChange();
                    }}
                    className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${statusFilter === key ? active : inactive}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${statusFilter === key ? 'bg-current opacity-60' : dot}`} />
                    {label}
                    <span className={`text-xs font-semibold tabular-nums ${statusFilter === key ? 'opacity-75' : 'opacity-60'}`}>
                      {count ?? 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Threads Table */}
            {(() => {
              const totalPages = totalTablePages;

              const pageNums = [];
              const delta = 2;
              for (let i = Math.max(1, safeTablePage - delta); i <= Math.min(totalPages, safeTablePage + delta); i++) {
                pageNums.push(i);
              }

              return (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  {loading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
                    </div>
                  ) : searchedPosts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                      <Layers className="w-12 h-12 text-gray-300 mb-3" />
                      <p className="text-lg font-medium">No threads found</p>
                      <p className="text-sm">Create your first thread to get started</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[900px] md:min-w-0 border-collapse">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <th className="w-14 px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider" />
                              <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                Post Content
                              </th>
                              <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                Threads Account
                              </th>
                              <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                <input
                                  ref={selectAllRef}
                                  type="checkbox"
                                  checked={isAllOnPageSelected}
                                  onChange={toggleSelectAllOnPage}
                                  className="h-4 w-4 cursor-pointer rounded accent-indigo-600"
                                  aria-label="Select all visible threads"
                                />
                              </th>
                              <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                Post Link
                              </th>
                              <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                Instagram
                              </th>
                              <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                                Scheduled
                              </th>
                              <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {pagedPosts.map((post, idx) => {
                              const account = accountsById[post.account_id];
                              return (
                                <tr
                                  key={post.id}
                                  className={`group border-b border-gray-100 transition-colors hover:bg-gray-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
                                >
                                  {/* Thumbnail */}
                                  <td className="w-14 px-3 py-2">
                                    <div className="w-11 h-11 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                                      {post.image_url ? (
                                        <img src={post.image_url} alt="" className="w-full h-full object-cover" />
                                      ) : (
                                        <ImageIcon className="w-4 h-4 text-gray-300" />
                                      )}
                                    </div>
                                  </td>

                                  {/* Post Content */}
                                  <td className="px-3 py-2 max-w-[220px]">
                                    <p
                                      className="text-sm text-gray-800 font-medium whitespace-nowrap overflow-hidden text-ellipsis"
                                      style={{ maxWidth: 210 }}
                                      title={post.caption || ''}
                                    >
                                      {truncateContent(post.caption, 32)}
                                    </p>
                                  </td>

                                  {/* Threads Account */}
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                                      {account?.profile_picture_url ? (
                                        <img
                                          src={account.profile_picture_url.replace(/&amp;/g, '&')}
                                          alt={account.name || account.username || 'Threads account'}
                                          referrerPolicy="no-referrer"
                                          className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                                        />
                                      ) : (
                                        <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold text-[10px] flex-shrink-0">
                                          {(account?.name || account?.username || 'T').charAt(0).toUpperCase()}
                                        </div>
                                      )}
                                      <span
                                        className="text-xs text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis"
                                        style={{ maxWidth: 110 }}
                                        title={account?.name || account?.username || ''}
                                      >
                                        {account?.name || account?.username || 'Unknown'}
                                      </span>
                                    </div>
                                  </td>

                                  {/* Status */}
                                  <td className="px-3 py-2">
                                    <span
                                      className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${getStatusBadge(post.status)}`}
                                      title={post.status === 'failed' ? (post.error_message || 'Failed') : ''}
                                    >
                                      {post.status?.charAt(0).toUpperCase() + post.status?.slice(1)}
                                    </span>
                                  </td>

                                  {/* Select */}
                                  <td className="px-3 py-2 text-center">
                                    <input
                                      type="checkbox"
                                      checked={selectedPostIds.has(post.id)}
                                      onChange={() => toggleSelectPost(post.id)}
                                      className="h-4 w-4 cursor-pointer rounded accent-indigo-600"
                                      aria-label={`Select thread ${post.id}`}
                                    />
                                  </td>

                                  {/* Post Link */}
                                  <td className="px-3 py-2">
                                    {post.post_url ? (
                                      <a
                                        href={post.post_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 whitespace-nowrap"
                                      >
                                        <LinkIcon className="w-3 h-3" />
                                        View
                                      </a>
                                    ) : (
                                      <span className="text-xs text-gray-300">—</span>
                                    )}
                                  </td>

                                  {/* Instagram */}
                                  <td className="px-3 py-2">
                                    {post.instagram_permalink ? (
                                      <a
                                        href={post.instagram_permalink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center text-gray-600 hover:text-gray-900"
                                        title="View on Instagram"
                                        aria-label="View on Instagram"
                                      >
                                        <Instagram className="w-4 h-4" />
                                      </a>
                                    ) : (
                                      <span className="text-xs text-gray-300">—</span>
                                    )}
                                  </td>

                                  {/* Scheduled */}
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    <span className="text-xs text-gray-500">{formatDateTime(post.scheduled_at)}</span>
                                  </td>

                                  {/* Actions */}
                                  <td className="px-3 py-2">
                                    <div className="flex items-center justify-center gap-0.5">
                                      <button
                                        onClick={() => handleViewPost(post)}
                                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                                        title="View on Threads"
                                      >
                                        <Eye className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={() => handleEditPost(post)}
                                        disabled={post.status !== 'scheduled'}
                                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                        title={post.status !== 'scheduled' ? 'Only scheduled threads can be edited' : 'Edit thread'}
                                      >
                                        <Edit className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={() => handleDeletePost(post.id)}
                                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                                        title="Delete"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50/60">
                          <p className="text-xs text-gray-400">
                            Showing {(safeTablePage - 1) * TABLE_PAGE_SIZE + 1}–{Math.min(safeTablePage * TABLE_PAGE_SIZE, searchedPosts.length)} of {searchedPosts.length} threads
                          </p>
                          <div className="flex items-center gap-1 w-full sm:w-auto overflow-x-auto">
                            <button
                              onClick={() => setTablePage((p) => Math.max(1, p - 1))}
                              disabled={safeTablePage === 1}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-600 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                              <ChevronLeft className="w-3.5 h-3.5" />
                              Prev
                            </button>

                            {pageNums[0] > 1 && (
                              <>
                                <button
                                  onClick={() => setTablePage(1)}
                                  className="px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition-colors"
                                >
                                  1
                                </button>
                                {pageNums[0] > 2 && <span className="px-1 text-xs text-gray-400">…</span>}
                              </>
                            )}

                            {pageNums.map((n) => (
                              <button
                                key={n}
                                onClick={() => setTablePage(n)}
                                className={`px-2.5 py-1.5 text-xs rounded-lg border transition-colors ${
                                  n === safeTablePage
                                    ? 'bg-gray-900 border-gray-900 text-white font-semibold'
                                    : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-600'
                                }`}
                              >
                                {n}
                              </button>
                            ))}

                            {pageNums[pageNums.length - 1] < totalPages && (
                              <>
                                {pageNums[pageNums.length - 1] < totalPages - 1 && (
                                  <span className="px-1 text-xs text-gray-400">…</span>
                                )}
                                <button
                                  onClick={() => setTablePage(totalPages)}
                                  className="px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition-colors"
                                >
                                  {totalPages}
                                </button>
                              </>
                            )}

                            <button
                              onClick={() => setTablePage((p) => Math.min(totalPages, p + 1))}
                              disabled={safeTablePage === totalPages}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-600 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                              Next
                              <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        </main>

        {/* ── New Thread Modal ─────────────────────────────────────────────── */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 overflow-hidden">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal} />

            <div className="relative w-full md:w-[95vw] max-w-[1600px] h-[calc(100dvh-1rem)] md:h-[92vh] bg-white rounded-xl md:rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fade-in">

              {/* Header */}
              <div className="flex-none border-b border-gray-100 px-3 py-3 md:px-6 md:py-4 flex items-center justify-between bg-white">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-[#232323] flex items-center justify-center flex-shrink-0 text-white">
                    <ThreadsLogoIcon size={18} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 leading-tight">New Thread / Instagram</h2>
                    <p className="text-xs text-gray-400 truncate">Select articles → set interval → generate schedule → post</p>
                  </div>
                </div>
                <button onClick={closeModal} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors flex-shrink-0">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden">

                {/* Left Panel — Article Browser */}
                <div className="w-full h-64 md:w-72 md:h-auto flex-none border-b md:border-b-0 md:border-r border-gray-100 flex flex-col overflow-hidden bg-gray-50/60">
                  <div className="flex-none p-4 border-b border-gray-100 bg-white">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Website</p>
                    <select
                      value={selectedWebsiteId}
                      onChange={(e) => handleWebsiteChange(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                    >
                      <option value="">-- Select a website --</option>
                      {websites.map((site) => (
                        <option key={site.id} value={site.id}>{site.siteName || site.siteUrl}</option>
                      ))}
                    </select>
                    {bulkQueue.length > 0 && (
                      <p className="mt-2 text-[11px] text-gray-600 font-medium">
                        {bulkQueue.length} article{bulkQueue.length !== 1 ? 's' : ''} selected
                      </p>
                    )}
                  </div>

                  <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {!selectedWebsiteId ? (
                      <div className="flex flex-col items-center justify-center h-full text-center px-4 gap-2">
                        <Globe className="w-8 h-8 text-gray-200" />
                        <p className="text-sm text-gray-400">Select a website to browse articles</p>
                      </div>
                    ) : articlesLoading && websiteArticles.length === 0 ? (
                      <div className="flex items-center justify-center py-10 gap-2 text-gray-400 text-sm">
                        <Spinner size={4} /> Loading…
                      </div>
                    ) : articlesError && websiteArticles.length === 0 ? (
                      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                        <p className="font-medium">Failed to load articles</p>
                        <p className="mt-1 break-words">{articlesError}</p>
                        <button
                          type="button"
                          onClick={() => loadArticles(selectedWebsiteId, 1, false)}
                          className="mt-2 font-medium underline"
                        >
                          Try again
                        </button>
                      </div>
                    ) : websiteArticles.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-8">No published WordPress articles found for this site</p>
                    ) : (
                      <>
                        {articlesError && (
                          <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700 break-words">
                            {articlesError}
                          </div>
                        )}
                        <label className="flex items-center gap-2 px-1 pb-1 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={allVisibleArticlesSelected}
                            onChange={handleSelectAllToggle}
                            className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-400"
                          />
                          <span className="text-xs font-medium text-gray-600">
                            Select All ({websiteArticles.length})
                          </span>
                        </label>

                        {websiteArticles.map((article) => {
                          const inQueue = bulkQueue.some((item) => item.article.id === article.id);
                          return (
                            <button
                              key={article.id}
                              type="button"
                              onClick={() => handleArticleToggle(article)}
                              className={`w-full flex gap-2.5 p-2.5 rounded-xl border-2 text-left transition-all hover:shadow-sm focus:outline-none ${
                                inQueue
                                  ? 'border-gray-800 bg-gray-50 shadow-sm'
                                  : 'border-gray-200 bg-white hover:border-gray-400'
                              }`}
                            >
                              <div className="w-11 h-11 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                                {article.image_main_url ? (
                                  <img
                                    src={article.image_main_url}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  />
                                ) : (
                                  <ImageIcon className="w-4 h-4 text-gray-300" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-snug">{article.title}</p>
                                {article.focus_keyword && (
                                  <p className="text-[10px] text-gray-400 mt-0.5 truncate">🔑 {article.focus_keyword}</p>
                                )}
                              </div>
                              {inQueue && (
                                <div className="flex-none w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center self-center">
                                  <Check className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </button>
                          );
                        })}

                        {hasMoreArticles && (
                          <button
                            type="button"
                            onClick={handleShowMore}
                            disabled={articlesLoading}
                            className="w-full py-2 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 flex items-center justify-center gap-1 transition-colors"
                          >
                            {articlesLoading ? <Spinner size={3} /> : 'Load more articles'}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Right Panel — Scheduler */}
                <div className="w-full min-w-0 flex-none md:flex-1 flex flex-col overflow-visible md:overflow-hidden">

                  {/* Controls bar */}
                  <div className="flex-none border-b border-gray-100 px-3 md:px-5 py-3 bg-white flex flex-col items-stretch gap-3 md:flex-row md:flex-nowrap md:items-center md:gap-x-4 md:overflow-x-auto">
                    <div className="flex flex-col items-stretch gap-1 md:flex-row md:items-center md:gap-2 md:flex-shrink-0">
                      <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Publish Start Date</label>
                      <input
                        type="datetime-local"
                        step={60}
                        value={publishStartDate}
                        onChange={(e) => setPublishStartDate(e.target.value)}
                        className="w-full min-w-0 md:w-auto border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                      />
                    </div>

                    <div className="flex flex-col items-stretch gap-1 md:flex-row md:items-center md:gap-2 md:flex-shrink-0">
                      <RefreshCw className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Posts Per Day</label>
                      <select
                        value={threadsPerDay}
                        onChange={(e) => setThreadsPerDay(Number(e.target.value))}
                        className="w-full min-w-0 md:w-44 md:flex-shrink-0 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white"
                      >
                        <option value={0}>Manual interval</option>
                        {Array.from({ length: 15 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>
                            {n} thread{n > 1 ? 's' : ''}/day — every {Math.round((24 * 60) / n)} min
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 md:flex-nowrap md:flex-shrink-0">
                      <label className={`text-xs font-medium whitespace-nowrap ${threadsPerDay > 0 ? 'text-gray-400' : 'text-gray-600'}`}>Interval</label>
                      <input
                        type="number" min={1}
                        value={threadsPerDay > 0 ? Math.round((24 * 60) / threadsPerDay) : bulkInterval}
                        onChange={(e) => setBulkInterval(Math.max(1, parseInt(e.target.value) || 1))}
                        disabled={threadsPerDay > 0}
                        className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                      />
                      <span className="text-xs whitespace-nowrap text-gray-400">min between posts</span>
                    </div>

                    <button
                      onClick={generateSchedule}
                      disabled={bulkQueue.length === 0 || !publishStartDate}
                      className="w-full md:w-auto justify-center md:ml-auto md:flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    >
                      <Zap className="w-4 h-4" /> Generate Schedule
                    </button>
                  </div>

                  {/* Queue */}
                  {bulkQueue.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-8">
                      <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                        <Layers className="w-8 h-8 text-gray-300" />
                      </div>
                      <p className="text-sm font-medium text-gray-500">No articles selected yet</p>
                      <p className="text-xs text-gray-400 max-w-xs">
                        Click articles on the left to add them to the schedule. Each article becomes one row.
                      </p>
                    </div>
                  ) : (
                    <div className="flex-1 overflow-auto">
                      <div className="min-w-[760px] md:min-w-0 sticky top-0 z-10 bg-gray-50 border-b border-gray-200 grid gap-3 px-4 py-2"
                           style={{ gridTemplateColumns: '56px 1fr 1fr 170px 36px' }}>
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider"></span>
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Caption</span>
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">First Comment (with link)</span>
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Scheduled Time</span>
                        <span></span>
                      </div>

                      <div className="divide-y divide-gray-100">
                        {bulkQueue.map((item, idx) => (
                          <div
                            key={item.id}
                            className="min-w-[760px] md:min-w-0 grid gap-3 px-4 py-3 items-start hover:bg-gray-50/60 transition-colors"
                            style={{ gridTemplateColumns: '56px 1fr 1fr 170px 36px' }}
                          >
                            <div className="flex flex-col items-center gap-1 pt-0.5">
                              <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                                {item.imageUrl ? (
                                  <img src={item.imageUrl} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <ImageIcon className="w-4 h-4 text-gray-300" />
                                )}
                              </div>
                              <span className="text-[10px] text-gray-400 font-semibold">#{idx + 1}</span>
                            </div>

                            {item.loading ? (
                              <div className="flex items-center gap-1.5 text-gray-400 text-xs py-3">
                                <Spinner size={3} /> Generating caption…
                              </div>
                            ) : (
                              <textarea
                                value={item.caption}
                                onChange={(e) => handleQueueUpdate(item.id, 'caption', e.target.value)}
                                rows={4}
                                placeholder="Caption for this thread…"
                                className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 resize-y focus:outline-none focus:ring-1 focus:ring-gray-400 text-gray-700 leading-relaxed"
                              />
                            )}

                            {item.loading ? (
                              <div className="flex items-center gap-1.5 text-gray-400 text-xs py-3">
                                <Spinner size={3} /> Generating…
                              </div>
                            ) : (
                              <textarea
                                value={item.firstComment}
                                onChange={(e) => handleQueueUpdate(item.id, 'firstComment', e.target.value)}
                                rows={4}
                                placeholder="Call-to-action + article link…"
                                className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 resize-y focus:outline-none focus:ring-1 focus:ring-gray-400 text-gray-500 leading-relaxed"
                              />
                            )}

                            <div className="pt-0.5">
                              <input
                                type="datetime-local"
                                value={item.scheduledTime}
                                onChange={(e) => handleQueueUpdate(item.id, 'scheduledTime', e.target.value)}
                                min={new Date().toISOString().slice(0, 16)}
                                className={`w-full text-xs border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-colors ${
                                  item.scheduledTime
                                    ? 'border-green-300 bg-green-50 text-green-800'
                                    : 'border-gray-200 text-gray-500'
                                }`}
                              />
                              {item.scheduledTime && (
                                <p className="text-[10px] text-green-600 mt-1 flex items-center gap-0.5">
                                  <Check className="w-2.5 h-2.5" /> Scheduled
                                </p>
                              )}
                            </div>

                            <div className="flex items-start pt-1">
                              <button
                                type="button"
                                onClick={() => handleRemoveRow(item.id)}
                                className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer — independent Threads/Instagram destinations + actions */}
              <div className="flex-none max-h-[44vh] md:max-h-none overflow-y-auto md:overflow-visible bg-white border-t border-gray-100 px-3 py-3 md:px-6 md:py-4 shadow-sm">
                <div className="mb-3">
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    Select Threads Account
                  </p>
                  {accountsLoading ? (
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <Spinner size={4} /> Loading accounts…
                    </div>
                  ) : accounts.length === 0 ? (
                    <div className="rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 p-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <p className="text-sm text-amber-800">
                        No Threads accounts connected —{' '}
                        <a href="/threads/accounts" className="font-semibold underline">connect one here</a>.
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {accounts.map((acc) => {
                        const selected = selectedAccountIds.includes(acc.id);
                        return (
                          <button
                            key={acc.id}
                            type="button"
                            onClick={() => toggleAccount(acc.id)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all text-sm ${
                              selected
                                ? 'border-gray-900 bg-gray-50 text-gray-900 shadow-sm'
                                : 'border-gray-200 text-gray-600 hover:border-gray-400 bg-white'
                            }`}
                          >
                            {acc.profile_picture_url ? (
                              <img src={acc.profile_picture_url} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-500 flex-shrink-0">
                                {(acc.username || 'T')[0].toUpperCase()}
                              </div>
                            )}
                            <span className="font-medium">{acc.name || acc.username}</span>
                            {selected && <Check className="w-3.5 h-3.5 text-gray-700" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="mb-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Instagram Account
                    </p>
                    {instagramAccountsLoading ? (
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <Spinner size={4} /> Loading Instagram accounts…
                      </div>
                    ) : instagramAccounts.length === 0 ? (
                      <p className="text-sm text-amber-700">No Instagram account connected.</p>
                    ) : (
                      <select
                        value={selectedInstagramAccountId}
                        onChange={(event) => setSelectedInstagramAccountId(event.target.value)}
                        className="w-full max-w-sm border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-pink-400"
                      >
                        {instagramAccounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            @{account.username || 'Instagram account'}
                            {account.token_expires_at && new Date(account.token_expires_at) <= new Date() ? ' — Token expired' : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Publish to:
                    </p>
                    <div className="flex flex-wrap items-center gap-5">
                      <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={publishToThreads}
                          onChange={(event) => setPublishToThreads(event.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-400"
                        />
                        Publish on Threads
                      </label>
                      <label className={`inline-flex items-center gap-2 text-sm font-medium ${
                        instagramAccounts.length === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 cursor-pointer'
                      }`}>
                        <input
                          type="checkbox"
                          checked={publishToInstagram}
                          onChange={(event) => setPublishToInstagram(event.target.checked)}
                          disabled={instagramAccountsLoading || instagramAccounts.length === 0}
                          className="w-4 h-4 rounded border-gray-300 text-pink-600 focus:ring-pink-400 disabled:cursor-not-allowed"
                        />
                        Publish on Instagram
                      </label>
                    </div>
                  </div>
                </div>

                {submitting && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium text-gray-600">Publishing selected destinations…</span>
                      <span className="text-xs font-bold text-gray-700">{submitProgress.done}/{submitProgress.total}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-gray-900 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${submitProgress.total > 0 ? (submitProgress.done / submitProgress.total) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-stretch md:items-center justify-between gap-4 flex-wrap">
                  <div className="w-full md:w-auto text-sm text-gray-500 flex flex-wrap items-center gap-3">
                    {bulkQueue.length > 0 ? (
                      <>
                        <span>
                          <span className="font-semibold text-gray-800">{bulkQueue.length}</span>{' '}
                          post{bulkQueue.length !== 1 ? 's' : ''} in queue
                        </span>
                        {scheduledCount > 0 && (
                          <span className="text-green-600 flex items-center gap-1">
                            <Check className="w-3.5 h-3.5" /> {scheduledCount} scheduled
                          </span>
                        )}
                        {publishToThreads && selectedAccountIds.length > 0 && (
                          <span className="text-gray-600">
                            → Threads: {selectedAccountIds.length} account{selectedAccountIds.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        {publishToInstagram && selectedInstagramAccountId && (
                          <span className="text-pink-600">→ Instagram: 1 account</span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400 text-xs italic">No articles selected</span>
                    )}
                  </div>

                  <div className="w-full md:w-auto flex flex-wrap items-center gap-2 md:gap-3">
                    <button
                      type="button"
                      onClick={closeModal}
                      disabled={submitting}
                      className="flex-1 md:flex-none justify-center px-5 py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors disabled:opacity-40"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSubmit(true)}
                      disabled={submitting || bulkQueue.length === 0 || !publishDestinationsReady}
                      className="flex-1 md:flex-none justify-center inline-flex items-center gap-2 px-4 md:px-6 py-2.5 rounded-lg border-2 border-gray-900 text-gray-900 font-medium text-sm hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {submitting && submitAction === 'publish' ? (
                        <><Spinner size={4} /> Publishing…</>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Publish Now
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSubmit(false)}
                      disabled={submitting || bulkQueue.length === 0 || !publishDestinationsReady}
                      className="w-full md:w-auto justify-center md:flex-none inline-flex items-center gap-2 px-4 md:px-6 py-2.5 rounded-lg bg-gray-900 text-white font-medium text-sm hover:bg-gray-700 transition-colors shadow-md disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {submitting && submitAction === 'schedule' ? (
                        <><Spinner size={4} color="white" /> Scheduling…</>
                      ) : (
                        <>
                          <Calendar className="w-4 h-4" />
                          Schedule {bulkQueue.length > 0 ? `${bulkQueue.length} Post${bulkQueue.length !== 1 ? 's' : ''}` : 'Posts'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Edit Thread Modal ────────────────────────────────────────────── */}
        {showEditModal && editingPost && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => { setShowEditModal(false); setEditingPost(null); }}
            />
            <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl animate-fade-in">
              <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
                <h2 className="text-xl font-semibold text-gray-900">Edit Scheduled Thread</h2>
                <button
                  onClick={() => { setShowEditModal(false); setEditingPost(null); }}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Caption</label>
                  <textarea
                    value={editCaption}
                    onChange={(e) => setEditCaption(e.target.value)}
                    rows={5}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Scheduled Time
                  </label>
                  <input
                    type="datetime-local"
                    value={editScheduledAt}
                    onChange={(e) => setEditScheduledAt(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
                <button
                  onClick={() => { setShowEditModal(false); setEditingPost(null); }}
                  className="px-5 py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={editSaving}
                  className="px-6 py-2.5 rounded-lg bg-gray-900 text-white font-medium text-sm hover:bg-gray-700 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {editSaving ? (
                    <><Spinner size={4} color="white" /> Saving...</>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SidebarProvider>
  );
}
